import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'sidebar_collapsed';

/**
 * Hook to manage sidebar collapse state with localStorage persistence
 *
 * @returns {Object} - { isCollapsed, toggle, setCollapsed }
 *
 * @example
 * const { isCollapsed, toggle } = useSidebarCollapse();
 */
export function useSidebarCollapse() {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    // Initialize from localStorage or default to false (expanded)
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : false;
  });

  // Persist to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));

    // Dispatch custom event for other components to react
    window.dispatchEvent(new CustomEvent('sidebar-collapse-change', {
      detail: { isCollapsed }
    }));
  }, [isCollapsed]);

  const toggle = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
  }, []);

  return {
    isCollapsed,
    toggle,
    setCollapsed,
  };
}
